import { useMemo, type CSSProperties, type MouseEvent as ReactMouseEvent } from 'react';

import type { RockDTO } from '@/api/endpoints';
import { ROCK_CELL, ROCK_SIZE, ROCK_URL, rockFrame } from '@/game/data/rockAtlas';
import { TORCH_U, TORCH_V } from '@/game/engine/lighting';
import { clipFor, layoutField, pickAt, type Embedding, type PlacedRock } from '@/game/engine/field';
import type { Viewport } from '@/game/engine/projection';
import { useRun } from '@/store/run';

/**
 * The rock the cave is made of.
 *
 * `CorridorRock` draws the one rock being worked, at full size with crack stages and
 * hit dots. This draws the other nineteen: the stone on the walls, the ceiling and the
 * floor that the lamp reaches. They are real server spawns — rolled, priced and gated
 * when they were generated — not scenery, which is the difference from the `WallRocks`
 * layer this sits in front of.
 *
 * Three slots, three ways of belonging to the cave. Wall and ceiling rock is a seam in
 * a surface, so it is centred on its point. **Floor rock is rubble resting on the
 * ground**, so it is anchored by its base, sunk slightly into the floor, and given a
 * contact shadow — without all three it reads as a pebble hovering at ankle height.
 * `blenderdoc/caveblender.png` banks broken stone along the base of every corridor it
 * shows, and it is most of what makes those frames look like a mine.
 *
 * Placement comes from the server (`slot`, `slot_u`, `slot_v`), never from a client
 * hash. Two rocks must occupy the same place on every device and across a reload, and
 * more importantly the position is part of what the player is choosing between, so it
 * has to be as authoritative as the payout.
 *
 * **No field rock can carry Shwe Kyat.** They are drawn from the trash pool, and
 * `rocks.trash_rock_never_grants_kyat` is a database CHECK constraint — the treasure
 * that can is the *featured* path rock, on its own unchanged pacing. Making the cave
 * denser therefore cannot make the withdrawable currency cheaper, which is the
 * property that let the field exist at all (docs/CAVE_VIEW.md §4).
 */
export const ROCK_COLS = ROCK_SIZE.width / ROCK_CELL;
export const ROCK_ROWS = ROCK_SIZE.height / ROCK_CELL;

/**
 * The torch, applied to a studio render.
 *
 * Every rock in the atlas was lit on a neutral backdrop by `tools/blender/studio.py`,
 * which is right for an inventory icon and wrong for something standing in a dark
 * warm cave. The sprite comes off that backdrop almost tonally flat — measured over
 * the loose-stone cell, every opaque pixel falls between luminance 77 and 139, with
 * no shaded side at all. `contrast` opens that range back up so the rock has a dark
 * face to turn away from the light; the warm shift is the cave's colour.
 */
function tint(tappable: boolean): string {
  /* Brighter than 1 because the torch layers now multiply and vignette these
     rocks along with the walls — measured at 4 m off-centre the vignette alone
     takes about 60% of the value. Left at the old sub-1 brightness the fix that
     put them under the light made them disappear into the wall, which is the
     opposite failure and just as unplayable.

     A rock inside reach is lit harder than one outside it. That is not a cheat
     laid over the lighting: reach is already a modelled fact (`inReach`), the
     in-reach rocks are the ones nearest the flame, and "which of these can I
     actually hit" is the only question this screen asks. Answering it with light
     beats answering it with a badge. */
  return tappable
    ? 'brightness(1.3) contrast(1.34) saturate(1.02) sepia(.2) hue-rotate(-8deg)'
    : 'brightness(1.0) contrast(1.2) saturate(.95) sepia(.18) hue-rotate(-8deg)';
}

/**
 * The shadow a rock casts, and the light that spills round it.
 *
 * `drop-shadow` and not `box-shadow`, which is the whole point. `box-shadow` is
 * drawn from the element's *border box* — a `rounded-[38%]` square — so a lumpy
 * rock silhouette got a soft rounded-rectangle plate behind it, offset 4px down and
 * blurred 12px, and wherever the torch lit the ground that plate was visible as a
 * sticker backing. Ablating it against a live capture is what finally identified it.
 * `drop-shadow` follows the sprite's alpha instead, so the shadow is rock-shaped.
 *
 * Direction comes from the torch (`TORCH_U`/`TORCH_V`), never from a fixed
 * `0 4px`: rock to the right of the flame throws its shadow further right, rock
 * below it throws down, and every rock on screen agrees about where the light is.
 * The second, warm shadow is the same offset negated — the torch catching the lip
 * of stone the rock is set into. That is what stops a wall or ceiling spawn from
 * reading as a sprite floating in front of the wall: it has nothing under it, so
 * the only cue available is the light breaking around its edge.
 *
 * Offsets scale with the rock, so a 34px far rock is not wearing a 92px rock's
 * shadow.
 */
export function rockShading(
  { x, y, size, view, worked, tappable }:
  { x: number; y: number; size: number; view: Viewport; worked: boolean; tappable: boolean },
): string {
  const dx = x - view.width * TORCH_U;
  const dy = y - view.height * TORCH_V;
  const len = Math.hypot(dx, dy) || 1;
  const ux = dx / len;
  const uy = dy / len;
  /** `k` of the rock's own width, away from the flame. */
  const off = (k: number) => `${(ux * size * k).toFixed(1)}px ${(uy * size * k).toFixed(1)}px`;

  return [
    tint(tappable),
    /* Two darks, because one cannot be both. The tight one is contact — it says
       the rock touches the surface it is on, and it is the whole answer to a
       wall or ceiling spawn that otherwise hangs in front of the wall with
       nothing holding it. The wide one is the ambient occlusion around it. */
    `drop-shadow(${off(0.05)} ${Math.max(1.5, size * 0.05).toFixed(1)}px rgba(0,0,0,.72))`,
    `drop-shadow(${off(0.14)} ${Math.max(3, size * 0.17).toFixed(1)}px rgba(0,0,0,.5))`,
    /* And the light breaking round the near edge, back toward the flame. */
    `drop-shadow(${off(-0.035)} ${(size * 0.06).toFixed(1)}px rgba(255,186,102,${tappable ? '.5' : '.3'}))`,
    /* Worked-but-unbroken rock keeps a warm rim, so a player who walked away
       mid-break can find it again on the way back. Also a drop-shadow now: as a
       `0 0 0 1.5px` ring it outlined the button box, which drew a glowing
       rounded square around a rock. */
    ...(worked ? ['drop-shadow(0 0 2px rgba(245,158,11,.95))'] : []),
  ].join(' ');
}

/**
 * The line where the stone leaves the rock face.
 *
 * `clipFor` cuts the buried half away, and a cut on its own looks like a rock sliced with
 * a razor — perfectly straight, perfectly clean, nothing holding it. Real stone breaking
 * out of a wall leaves two things at the join: a dark crack where the two surfaces meet,
 * and a lit lip on the exposed side where the light catches the broken edge.
 *
 * **The lit half is the one that does the work here.** A dark crack on a near-black cave
 * wall is invisible — the same lesson as the floor contact cue below, and the reason the
 * rubble shadow is a warm bounce rather than a black ellipse. The dark stop is kept
 * anyway, hard against the cut, because it is what stops the warm lip from reading as a
 * glow *around* the rock instead of a highlight *on* it.
 *
 * The strip is laid along the cut with `inset` offsets rather than a transform, so it
 * lands in the same coordinate space as the clip and cannot drift out of register with
 * it. It sits outside the spinning inner layer for the same reason the clip does: the
 * wall does not turn with the rock.
 */
export function seamStyle(embed: Embedding, size: number): CSSProperties {
  const thickness = Math.max(2, size * 0.1);
  const cut = `${(embed.fraction * 100).toFixed(2)}%`;
  /* Away from the cut, into the exposed stone: dark at the join, warm just past it,
     gone by the time it reaches the middle of the rock. */
  const ramp = (to: string) =>
    `linear-gradient(to ${to}, rgba(0,0,0,.6) 0%, rgba(255,186,102,.24) 42%,` +
    ' rgba(255,186,102,.08) 72%, rgba(255,186,102,0) 100%)';

  /*
   * Faded to nothing at both ends, and this is not decoration.
   *
   * The sprite is a lump inside a square box, so a seam run at the box's full height is
   * a straight lit bar sticking out past the top and bottom of the stone — measured
   * against a live capture it read as a pole planted in the wall, which is worse than
   * having no seam at all. The mask keeps the lip inside the silhouette it belongs to.
   */
  const fade = (to: string) =>
    `linear-gradient(to ${to}, transparent 0%, #000 26%, #000 74%, transparent 100%)`;

  if (embed.axis === 'y') {
    /* Ceiling: buried above, exposed below. */
    return {
      left: '18%', right: '18%', top: cut, height: thickness,
      background: ramp('bottom'),
      maskImage: fade('right'),
      WebkitMaskImage: fade('right'),
    };
  }
  const side = embed.side < 0 ? { left: cut } : { right: cut };
  return {
    ...side, top: '18%', bottom: '18%', width: thickness,
    background: ramp(embed.side < 0 ? 'right' : 'left'),
    maskImage: fade('bottom'),
    WebkitMaskImage: fade('bottom'),
  };
}

/**
 * Fog, as a curve rather than as raw alpha.
 *
 * `projection.opacity` is linear in depth over the lamp's whole reach, which is
 * the right shape for *fog* and the wrong shape for *transparency*. With a candle
 * (12 m of reach) it makes a rock two metres away 17% see-through and a rock at
 * four metres 33% see-through — close enough to touch and you can read the wall
 * through the stone. Measured against the wall behind it, a lit rock at 4 m came
 * out at 1.8:1; most of what was missing was simply the rock not being there.
 *
 * So: solid up close, fading only once it is genuinely far. Same endpoints as
 * before — 1 at the camera, 0 at the edge of the lamp — so nothing about the lamp
 * fantasy or the cull changes, only the middle of the curve.
 *
 *   linear  2 m .83   4 m .67   8 m .33
 *   here    2 m .98   4 m .91   8 m .55
 */
export function fogAlpha(linear: number): number {
  return 1 - Math.pow(1 - Math.max(0, Math.min(1, linear)), 2.2);
}

/**
 * How damaged a rock looks, 0–4.
 *
 * The same arithmetic the focused view uses, so one rock cannot show two different
 * amounts of damage depending on which screen you are looking at. `required_hits` is
 * withheld for a locked rock — there is nothing to show progress against, and a rock
 * you cannot swing at has none anyway.
 */
export function crackStageOf(hitsRegistered: number, requiredHits: number | null): number {
  if (!requiredHits || requiredHits <= 0) return 0;
  return Math.max(0, Math.min(4, Math.ceil((hitsRegistered / requiredHits) * 4)));
}

/**
 * A rock's cell in the atlas, as background percentages.
 *
 * Exported so `RockDebris` draws the falling rock from the same frame the wall was
 * drawing a moment earlier — two lookups would eventually disagree, and the disagreement
 * would show up as the rock changing material as it comes loose.
 */
export function rockSprite(materialKey: string, stage: number) {
  const frame = rockFrame(`renders/rock_${materialKey}_${Math.max(0, Math.min(4, stage))}`);
  if (!frame) return null;
  return {
    px: ROCK_COLS > 1 ? (frame.x / (ROCK_SIZE.width - ROCK_CELL)) * 100 : 0,
    py: ROCK_ROWS > 1 ? (frame.y / (ROCK_SIZE.height - ROCK_CELL)) * 100 : 0,
  };
}

export function RockField({ spawns, depth, view, lightPx, activeUuid, onPick }: {
  spawns: RockDTO[];
  /** The player's depth, so each spawn's absolute depth becomes a relative one. */
  depth: number;
  view: Viewport;
  lightPx: number;
  activeUuid: string | null;
  onPick: (rock: RockDTO) => void;
}) {
  const focusRock = useRun((s) => s.focusRock);

  /* Laid out once, in paint order, before anything is drawn. That the layout exists as
     a value rather than only as a side effect of rendering is what lets a tap be
     resolved against it — see `pickAt`. */
  const placed = useMemo(
    () => layoutField(spawns, depth, view, lightPx, activeUuid),
    [spawns, depth, view, lightPx, activeUuid],
  );

  /**
   * Resolve a tap against the silhouettes, not the DOM boxes.
   *
   * The browser hands the event to the topmost element whose *box* contains the point,
   * and a rock's box is a `rounded-[38%]` square around a lumpy sprite. Three rocks
   * spawn at every second metre, so those boxes overlap constantly and the rock that
   * receives the click is sometimes not the one under the player's finger — measured at
   * roughly 1 tap in 100 on rock that is plainly visible and in reach.
   *
   * One percent sounds small and is not: the rock chosen IS the payout chosen, and the
   * player has no way to tell it happened. They aimed at a Quartz vein, mined the loose
   * stone beside it, and the only explanation available to them is "I must have missed".
   *
   * Falls back to the rock that was clicked when nothing covers the point — that can
   * only mean the tap landed in the transparent margin of exactly one rock, and honouring
   * it is friendlier than swallowing the tap.
   */
  const pick = (fallback: PlacedRock, event: ReactMouseEvent<HTMLButtonElement>) => {
    const host = event.currentTarget.parentElement?.getBoundingClientRect();
    const chosen = host
      ? pickAt(placed, event.clientX - host.left, event.clientY - host.top)
      : null;
    const rock = (chosen ?? fallback).rock;
    focusRock(rock);
    onPick(rock);
  };

  return (
    <>
      {placed.map((it) => {
        const { rock, size, spin, flip, onFloor, tappable } = it;
        const stage = crackStageOf(rock.hits_registered, rock.required_hits);
        const cell = rockSprite(rock.material?.key ?? 'loose', stage);
        const a = rock.material?.color_a ?? '#6E5240';
        const b = rock.material?.color_b ?? '#2C1E16';
        const worked = stage > 0;

        /*
         * Position comes from `layoutField` now, not from a transform re-derived here.
         *
         * This used to translate off the corridor point with its own percentages, which
         * meant the box the player taps was computed twice from two different rules —
         * exactly the split that `field.ts` exists to close. `pickAt` measures against
         * `left`/`top`, so the element has to be drawn at `left`/`top`.
         *
         * The spin is NOT on this element. `clip-path` is applied in the element's own
         * box and transforms apply after it, so rotating here would rotate the cut with
         * the rock — and the cut is the wall, which does not turn. Spin lives on the
         * inner layer instead.
         */
        const clip = clipFor(it.embed);

        const label = rock.locked
          ? `Rock, needs axe level ${rock.required_axe_level}`
          : `Mine the rock at ${rock.depth_m ?? depth} metres`;

        return (
          <button
            key={rock.spawn_uuid}
            type="button"
            disabled={!tappable}
            onClick={(event) => { if (tappable) pick(it, event); }}
            className="pointer-events-auto absolute rounded-[38%] transition-opacity duration-200
                       disabled:pointer-events-none disabled:cursor-default"
            style={{
              left: it.left,
              top: it.top,
              width: size,
              height: size,
              opacity: fogAlpha(it.opacity) * (rock.locked ? 0.55 : 1),
              zIndex: it.z,
              /* The rock face. Everything on the far side of this line is inside the wall
                 and is neither drawn nor tappable — see `embedding` and `coversPoint`. */
              clipPath: clip,
              WebkitClipPath: clip,
            }}
          >
            {/* The stone itself. Separate from the button because the spin belongs to the
                rock and the clip belongs to the wall, and CSS applies clip-path before
                transforms — so with both on one element the cut would turn with the rock. */}
            <i
              aria-hidden
              className="absolute inset-0 block rounded-[38%]"
              style={{
                transform: `rotate(${spin}deg) scaleX(${flip})`,
                WebkitTransform: `rotate(${spin}deg) scaleX(${flip})`,
                /* The atlas render when there is one, a two-stop lump when there is
                   not — the atlas does not cover every material yet, and a missing
                   sprite must degrade to a rock rather than to nothing. */
                ...(cell
                  ? {
                      backgroundImage: `url(${ROCK_URL})`,
                      backgroundSize: `${ROCK_COLS * 100}% ${ROCK_ROWS * 100}%`,
                      backgroundPosition: `${cell.px}% ${cell.py}%`,
                    }
                  : { background: `radial-gradient(circle at 34% 30%, ${a}, ${b} 72%)` }),
                /* Lit by the torch, not by the studio — and shaded away from it.
                   See `rockShading`. This layer also sits *under* TorchLight now
                   (Corridor.tsx), so the cone, the warm core and the vignette all
                   fall on these rocks the way they fall on the walls; the filter
                   only has to supply what a light pass cannot, which is a tonal
                   range the render never had. */
                filter: rockShading({ x: it.x, y: it.y, size, view, worked, tappable }),
              }}
            />

            {/* The seam where the stone meets the face.
                A cut with nothing along it looks like a rock sliced by a razor. Real
                stone breaking out of a wall leaves a lit lip on the exposed side and a
                dark line where the two surfaces meet — and on this palette the lit half
                is what does the work, because a dark line on a near-black wall is
                invisible (the same lesson as the floor contact cue below). */}
            {it.embed && (
              <i
                aria-hidden
                className="pointer-events-none absolute block"
                style={seamStyle(it.embed, size)}
              />
            )}
            {/* The contact cue, and it is deliberately NOT a shadow.
                A black ellipse under a rock is the standard fix and it did nothing
                here: the cave floor is already near-black, so darkness on darkness is
                invisible and the rubble floated anyway. On an unlit floor the thing
                that reads as contact is LIGHT — the torch catching the ground the rock
                is sitting in. So this is a warm bounce, with a tight dark core right at
                the base to keep the rock from glowing off the floor. */}
            {onFloor && (
              <i
                aria-hidden
                className="pointer-events-none absolute left-1/2 bottom-0 block rounded-[50%]"
                style={{
                  /* Tight and faint. At 150% wide and .22 alpha this read as a lit
                     plate under each rock — a sticker halo, most visible exactly
                     where the torch already lights the floor. It only has to say
                     "this touches the ground", so it stays inside the silhouette. */
                  width: '104%',
                  height: `${Math.max(3, size * 0.2)}px`,
                  transform: `translate3d(-50%, 22%, 0) scaleX(${flip}) rotate(${-spin}deg)`,
                  background:
                    'radial-gradient(ellipse at center, rgba(255,186,102,.13) 0%,' +
                    ' rgba(90,54,20,.12) 52%, rgba(0,0,0,0) 76%)',
                }}
              />
            )}
            <span className="sr-only">{label}</span>
          </button>
        );
      })}
    </>
  );
}
